/**
 * Colour maths. Every colour in PRISM funnels through here.
 *
 * Working space is OKLab: perceptually even, so a blend of two pigments lands
 * where the eye expects and a distance of N means the same thing at every hue.
 * sRGB is only ever the display edge and the storage edge.
 *
 * Axes: L in [0,1], a and b roughly [-0.4, 0.4].
 */

export interface Lab {
  L: number;
  a: number;
  b: number;
}

/** Cylindrical OKLab. Hue in degrees [0,360). */
export interface Lch {
  L: number;
  C: number;
  h: number;
}

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** Distances are OKLab euclidean units scaled by this, so 1 unit ≈ 1 JND. */
export const DELTA_E_SCALE = 100;

const TAU = Math.PI * 2;

// ---------------------------------------------------------------------------
// sRGB transfer
// ---------------------------------------------------------------------------

export function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

export function linearToSrgb(c: number): number {
  return c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
}

const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);

// ---------------------------------------------------------------------------
// OKLab <-> linear sRGB
// ---------------------------------------------------------------------------

/** Linear sRGB (0..1) to OKLab. */
export function linearToOklab(r: number, g: number, b: number): Lab {
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;

  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);

  return {
    L: 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_,
    a: 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
    b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_,
  };
}

/** OKLab to linear sRGB. May fall outside [0,1] for out-of-gamut colours. */
export function oklabToLinear({ L, a, b }: Lab): Rgb {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.291485548 * b;

  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;

  return {
    r: 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    g: -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    b: -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  };
}

// ---------------------------------------------------------------------------
// Lch helpers
// ---------------------------------------------------------------------------

export function labToLch({ L, a, b }: Lab): Lch {
  const C = Math.hypot(a, b);
  let h = (Math.atan2(b, a) * 360) / TAU;
  if (h < 0) h += 360;
  return { L, C, h };
}

export function lchToLab({ L, C, h }: Lch): Lab {
  const rad = (h * TAU) / 360;
  return { L, a: Math.cos(rad) * C, b: Math.sin(rad) * C };
}

// ---------------------------------------------------------------------------
// sRGB <-> OKLab
// ---------------------------------------------------------------------------

export function rgbToOklab(rgb: Rgb): Lab {
  return linearToOklab(
    srgbToLinear(rgb.r / 255),
    srgbToLinear(rgb.g / 255),
    srgbToLinear(rgb.b / 255),
  );
}

/**
 * OKLab to 8-bit sRGB, gamut-mapped.
 *
 * Out-of-gamut colours are pulled in by reducing chroma at constant lightness
 * and hue, which is the least surprising way to lose something unreachable.
 * Returns 0..255 integers.
 */
export function oklabToRgb(lab: Lab): Rgb {
  const linear = oklabToLinear(lab);
  if (inGamut(linear)) return to255(linear);

  // Lightness is preserved in preference to chroma: a clipped L is far more
  // visible than a slightly duller colour.
  const lch = labToLch(lab);
  let lo = 0;
  let hi = lch.C;
  for (let i = 0; i < 16; i++) {
    const mid = (lo + hi) / 2;
    if (inGamut(oklabToLinear(lchToLab({ ...lch, C: mid })))) lo = mid;
    else hi = mid;
  }
  const mapped = oklabToLinear(lchToLab({ ...lch, C: lo }));
  return to255(mapped);
}

function inGamut(c: Rgb): boolean {
  const eps = 1e-5;
  return (
    c.r >= -eps && c.r <= 1 + eps &&
    c.g >= -eps && c.g <= 1 + eps &&
    c.b >= -eps && c.b <= 1 + eps
  );
}

/** Linear sRGB to 8-bit display sRGB. The transfer curve belongs here. */
function to255(c: Rgb): Rgb {
  return {
    r: Math.round(clamp01(linearToSrgb(c.r)) * 255),
    g: Math.round(clamp01(linearToSrgb(c.g)) * 255),
    b: Math.round(clamp01(linearToSrgb(c.b)) * 255),
  };
}

// ---------------------------------------------------------------------------
// Hex
// ---------------------------------------------------------------------------

export function hexToRgb(hex: string): Rgb {
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  h = h.padEnd(6, '0').slice(0, 6);

  // Anything unparseable degrades to 0 rather than poisoning the whole
  // pipeline with NaN.
  const byte = (s: string) => {
    const v = parseInt(s, 16);
    return Number.isNaN(v) ? 0 : Math.min(255, Math.max(0, v));
  };
  return { r: byte(h.slice(0, 2)), g: byte(h.slice(2, 4)), b: byte(h.slice(4, 6)) };
}

export function rgbToHex({ r, g, b }: Rgb): string {
  return '#' + [r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('');
}

export function hexToOklab(hex: string): Lab {
  return rgbToOklab(hexToRgb(hex));
}

export function oklabToHex(lab: Lab): string {
  return rgbToHex(oklabToRgb(lab));
}

export function labToCss(lab: Lab): string {
  const { r, g, b } = oklabToRgb(lab);
  return `rgb(${r} ${g} ${b})`;
}

// ---------------------------------------------------------------------------
// Distance, blending, contrast
// ---------------------------------------------------------------------------

/** Perceptual distance in scaled OKLab units. 1 unit is roughly one JND. */
export function deltaE(a: Lab, b: Lab): number {
  return Math.hypot(a.L - b.L, a.a - b.a, a.b - b.b) * DELTA_E_SCALE;
}

export function mixLab(a: Lab, b: Lab, t = 0.5): Lab {
  return {
    L: a.L + (b.L - a.L) * t,
    a: a.a + (b.a - a.a) * t,
    b: a.b + (b.b - a.b) * t,
  };
}

/** Relative luminance on linear sRGB. Used for contrast decisions only. */
export function relativeLuminance(lab: Lab): number {
  const { r, g, b } = oklabToLinear(lab);
  return 0.2126 * clamp01(r) + 0.7152 * clamp01(g) + 0.0722 * clamp01(b);
}

/** True when a colour is dark enough to want light text on it. */
export function isDark(lab: Lab): boolean {
  return relativeLuminance(lab) < 0.36;
}

/** Hue family word for a11y labels and generated names. */
export function hueFamily(lab: Lab): string {
  const { C, h } = labToLch(lab);
  if (C < 0.035) {
    if (lab.L > 0.85) return 'white';
    if (lab.L > 0.62) return 'ecru';
    if (lab.L > 0.4) return 'grey';
    if (lab.L > 0.22) return 'slate';
    return 'black';
  }
  // Boundaries sit on OKLab hue angles, not sRGB ones. Pure red is at 29
  // degrees here, pure yellow at 110, pure blue at 264 — so a band table
  // copied from an HSL wheel names every colour one step off.
  const bands: Array<[number, string]> = [
    [16, 'rose'],
    [42, 'crimson'],
    [68, 'vermilion'],
    [92, 'saffron'],
    [118, 'lime'],
    [136, 'green'],
    [162, 'jade'],
    [190, 'cyan'],
    [215, 'cerulean'],
    [238, 'azure'],
    [262, 'blue'],
    [290, 'violet'],
    [315, 'magenta'],
    [361, 'rose'],
  ];
  for (const [max, name] of bands) if (h < max) return name;
  return 'rose';
}

// ---------------------------------------------------------------------------
// Gamut-relative chroma
// ---------------------------------------------------------------------------

/**
 * Maximum in-gamut chroma at a given lightness and hue.
 *
 * Absolute chroma is a biased yardstick: yellow can reach 0.2 while blue tops
 * out near 0.13, so "pick the most saturated" quietly starves the blues. This
 * is the normaliser that makes hues comparable.
 */
const LUT_L = 25; // lightness cells
const LUT_H = 48; // hue cells, 7.5 degrees apart
let chromaLut: Float32Array | null = null;

function buildChromaLut(): Float32Array {
  const lut = new Float32Array(LUT_L * LUT_H);
  for (let li = 0; li < LUT_L; li++) {
    const L = li / (LUT_L - 1);
    for (let hi = 0; hi < LUT_H; hi++) {
      const h = (hi / LUT_H) * 360;
      let lo = 0;
      let hiC = 0.45;
      for (let i = 0; i < 11; i++) {
        const mid = (lo + hiC) / 2;
        if (inGamut(oklabToLinear(lchToLab({ L, C: mid, h })))) lo = mid;
        else hiC = mid;
      }
      lut[li * LUT_H + hi] = lo;
    }
  }
  return lut;
}

export function maxChromaFor(L: number, h: number): number {
  if (!chromaLut) chromaLut = buildChromaLut();
  const lut = chromaLut;

  const lc = Math.min(LUT_L - 1, Math.max(0, L * (LUT_L - 1)));
  const hc = ((h % 360) / 360) * LUT_H;
  const l0 = Math.floor(lc);
  const h0 = Math.floor(hc) % LUT_H;
  const l1 = Math.min(LUT_L - 1, l0 + 1);
  const h1 = (h0 + 1) % LUT_H;
  const lt = lc - l0;
  const ht = hc - Math.floor(hc);

  const a = lut[l0 * LUT_H + h0];
  const b = lut[l0 * LUT_H + h1];
  const c = lut[l1 * LUT_H + h0];
  const d = lut[l1 * LUT_H + h1];

  return a * (1 - lt) * (1 - ht) + b * (1 - lt) * ht + c * lt * (1 - ht) + d * lt * ht;
}

/** Chroma as a fraction of the most that hue and lightness could reach. */
export function relativeChroma(lab: Lab): number {
  const { L, C, h } = labToLch(lab);
  const max = maxChromaFor(L, h);
  if (max < 0.012) return 0;
  return Math.min(1, C / max);
}

export function lightnessWord(L: number): string {
  if (L < 0.26) return 'inky';
  if (L < 0.4) return 'deep';
  if (L < 0.58) return 'true';
  if (L < 0.76) return 'soft';
  if (L < 0.9) return 'pale';
  return 'bright';
}
